import { useEffect, useRef, useState } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import { Visibility, Lock } from '@mui/icons-material'
import { chatApi } from '@api/endpoints/chat'
import { getImageUrl } from '@utils/imageUtils'
import { useAutoDisplayForConversation } from '@store/imageDisplayPrefStore'
import type { ChatMessageDto } from '@api/types/chat'
import { EphemeralImageDialog } from './EphemeralImageDialog'
import { openChatImage, ProtectedImageLoadError, type OpenedImage } from '../protectedImage'

interface ChatImageProps {
  message: ChatMessageDto
  /** The current user sent this message (their own view-limited photo opens uncounted). */
  isOwn?: boolean
}

const IMG_STYLE: React.CSSProperties = {
  maxWidth: '100%',
  maxHeight: 300,
  borderRadius: 8,
  cursor: 'pointer',
  display: 'block',
}

/** Placeholder shown before an image is revealed (privacy tap-to-show, or a view-limited image). */
function Placeholder({
  label,
  sublabel,
  locked,
  loading,
  onClick,
}: {
  label: string
  sublabel?: string
  locked?: boolean
  loading?: boolean
  onClick?: () => void
}) {
  return (
    <Box
      onClick={locked || loading ? undefined : onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        maxWidth: '100%',
        borderRadius: 999,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        px: 1.5,
        py: 0.75,
        cursor: locked || loading ? 'default' : 'pointer',
        color: 'text.secondary',
      }}
    >
      {loading ? (
        <CircularProgress size={16} />
      ) : locked ? (
        <Lock fontSize="small" />
      ) : (
        <Visibility fontSize="small" />
      )}
      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
        {label}
      </Typography>
      {sublabel && (
        <Typography variant="caption" color="text.disabled" noWrap>
          · {sublabel}
        </Typography>
      )}
    </Box>
  )
}

/**
 * Renders a chat message's image with two gates:
 * - Viewer privacy: when the user's auto-display preference is off, normal images require a tap to
 *   reveal (no server call — the URL is already present).
 * - Ephemeral (view-limited) images: always tap-to-reveal and never inline. Each reveal spends one of
 *   the sender's allotted per-viewer views; the server answers with a one-time token, the bytes are
 *   fetched with it and shown from memory (a blob: URL revoked when the viewer closes). The sender
 *   previews their own the same way, uncounted. There is no address for these images anywhere.
 */
export function ChatImage({ message, isOwn = false }: ChatImageProps) {
  // This conversation's answer, which is the global default unless it has been given its own.
  const autoDisplay = useAutoDisplayForConversation(message.conversationId)
  const [revealed, setRevealed] = useState(false)
  const [remaining, setRemaining] = useState<number | null>(message.imageViewsRemaining ?? null)
  const [archived, setArchived] = useState<boolean>(message.isImageArchived ?? false)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  // When set, the view-limited image is shown FULL SCREEN (not inline), from memory.
  const [viewer, setViewer] = useState<OpenedImage | null>(null)

  // The in-memory copy goes when the viewer closes, and if the message unmounts with it open.
  const viewerRef = useRef<OpenedImage | null>(null)
  useEffect(() => {
    viewerRef.current = viewer
  }, [viewer])
  useEffect(() => () => viewerRef.current?.release(), [])

  const closeViewer = () => {
    viewer?.release()
    setViewer(null)
  }

  const isLimited = message.imageMaxViews != null

  const viewerNode = viewer && (
    <EphemeralImageDialog
      uri={viewer.url}
      // The sender's own preview is uncounted: no "views left" chip.
      remaining={isOwn ? null : viewer.remaining}
      seconds={message.imageViewSeconds}
      onClose={closeViewer}
    />
  )

  // Normal image: the URL is in the message.
  if (!isLimited) {
    if (!message.imageUrl) return null
    const url = getImageUrl(message.imageUrl) ?? message.imageUrl
    if (autoDisplay || revealed) {
      return <img src={url} alt="Chat image" style={IMG_STYLE} onClick={() => window.open(url, '_blank')} />
    }
    return <Placeholder label="Tap to view image" onClick={() => setRevealed(true)} />
  }

  // Limited image: the sender previews their own (uncounted); a recipient's reveal spends a view.
  const open = async () => {
    setLoading(true)
    setFailed(false)
    try {
      const opened = await openChatImage(message.id, chatApi)
      setViewer(opened)
      if (!isOwn) setRemaining(opened.remaining)
    } catch (error) {
      if (error instanceof ProtectedImageLoadError) {
        // The view may have been spent, but the photo never arrived: say so, don't lock it.
        if (!isOwn && error.remaining != null) setRemaining(error.remaining)
        setFailed(true)
      } else {
        // 403 exhausted (or gone) — lock it.
        setArchived(true)
      }
    } finally {
      setLoading(false)
    }
  }

  if (isOwn) {
    const max = message.imageMaxViews ?? 0
    return (
      <>
        <Placeholder
          label={failed ? "Couldn't load, tap to retry" : 'Your photo'}
          sublabel={`${max}-view limit`}
          loading={loading}
          onClick={open}
        />
        {viewerNode}
      </>
    )
  }

  const left = remaining ?? message.imageMaxViews ?? 0
  const inline =
    archived || remaining === 0 ? (
      <Placeholder label="No views remaining" locked />
    ) : (
      <Placeholder
        label={failed ? "Couldn't load, tap to try again" : 'Tap to view'}
        sublabel={`${left} view${left === 1 ? '' : 's'} left`}
        loading={loading}
        onClick={open}
      />
    )

  return (
    <>
      {inline}
      {viewerNode}
    </>
  )
}
